/* eslint-disable react-refresh/only-export-components */
import { useRecordContext } from "ra-core";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

import type { Company } from "../types";

/**
 * Initials of a company name, e.g. "Sliedrecht Sport" -> "SS".
 * Words that do not start with a letter or a digit (dashes, "&", "-") are skipped.
 */
export const getCompanyInitials = (name?: string | null): string => {
  const words = (name ?? "")
    .split(/\s+/)
    .filter((word) => /^[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return "?";
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();
};

/**
 * Initials-only company avatar. There are no company logos in this app, so the
 * initials (coloured by <AvatarFallback>) are the visual anchor for a company.
 */
export const CompanyAvatar = (props: {
  record?: Company;
  width?: 20 | 40;
  height?: 20 | 40;
  className?: string;
}) => {
  const { width = 40, className } = props;
  const record = useRecordContext<Company>(props);
  if (!record) return null;

  const isSmall = width !== 40;

  return (
    <Avatar
      className={cn(isSmall ? "w-5 h-5" : "w-10 h-10", className)}
      title={record.name}
    >
      <AvatarFallback
        className={cn("font-medium", isSmall ? "text-[10px]" : "text-sm")}
      >
        {getCompanyInitials(record.name)}
      </AvatarFallback>
    </Avatar>
  );
};
